import { useTokenizedApi } from '@tokenized/sdk-react-private';
import { Instrument } from '@tokenized/sdk-js-private';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { VALID_HANDLE_DOMAINS } from '#modules/api/User';

const { round } = Math;

export const QUIET_VALIDATION_FAILURE = '-';
export const IMMEDIATE_VALIDATION_SUCCESS = '*';

function transformImmediateSuccess(result) {
  if (result === IMMEDIATE_VALIDATION_SUCCESS) {
    return undefined;
  }
  return result;
}

export function useValidators(...validators) {
  const intl = useIntl();
  const actualValidators = validators.filter((validator) => !!validator);
  return useCallback(
    // Try a list of validators (passing intl as the first
    // arg to each one), and return the first error, or
    // undefined if they all pass
    (...validateArgs) => {
      const result = actualValidators.reduce(
        (error, validator) =>
          // eslint-disable-next-line promise/no-promise-in-callback
          error?.then?.(
            (asyncError) => asyncError || validator(intl, ...validateArgs),
          ) ||
          error ||
          validator(intl, ...validateArgs),
        undefined,
      );
      return (
        result?.then?.(transformImmediateSuccess) ||
        transformImmediateSuccess(result)
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, ...validators],
  );
}

// Any truthy value (so any string other than ''), or any number (including 0)
export const fieldIsRequired = (intl, value) =>
  value || typeof value === 'number'
    ? undefined
    : intl.formatMessage({
        defaultMessage: 'This field is required.',
        description: 'Form field validation failure: a value is required',
      });

// Use when making the input go red and show an error would be annoying,
// but you do want the form to be invalid if the field is blank
export const fieldIsRequiredQuiet = (intl, value) =>
  value || typeof value === 'number' ? undefined : QUIET_VALIDATION_FAILURE;

// Use for a checkbox field that must be checked: e.g. acknowledge
// terms of service.
export const fieldIsChecked = (intl, value) =>
  value === true ? undefined : QUIET_VALIDATION_FAILURE;

// Any Instrument (with a qunatity), number, or any string that converts to a number
export const fieldIsNumber = (intl, value) => {
  value = Instrument.toAmount(value);
  if (typeof value === 'number' && !isNaN(value)) {
    return undefined;
  }
  if (typeof value === 'string' && value !== '' && !isNaN(value)) {
    return undefined;
  }
  return intl.formatMessage({
    defaultMessage: 'Enter a number.',
    description: 'Form field validation failure: a number is required',
  });
};

export const fieldIsNumberOptional = (intl, value) => {
  value = Instrument.toAmount(value);
  if (typeof value === 'number' && !isNaN(value)) {
    return undefined;
  }
  if (typeof value === 'string' && value !== '' && !isNaN(value)) {
    return undefined;
  }
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  return intl.formatMessage({
    defaultMessage: 'Enter a number.',
    description: 'Form field validation failure: a number is required',
  });
};

export function isValidHandle(handle) {
  if (!handle) {
    return false;
  }
  const handleParts = handle.split('@') ?? [];
  if (handleParts.length !== 2) {
    return false;
  }
  const [domain] = handleParts.slice(-1);
  if (VALID_HANDLE_DOMAINS.includes(domain)) {
    return true;
  }
}

export function isNotHandle(intl, handle) {
  if (!handle) {
    return undefined;
  }
  if (isValidHandle(handle)) {
    return intl.formatMessage({
      defaultMessage:
        'Paymail handles are not supported for this transfer type',
      description: 'Form field validation failure: handle provided',
    });
  }
}

export function handleIsValid(intl, handle) {
  if (!handle) {
    return undefined;
  }
  if (!isValidHandle(handle)) {
    return intl.formatMessage({
      defaultMessage: 'Enter a valid handle.',
      description: 'Form field validation failure: invalid handle',
    });
  }
}

// Anything that converts to a number >= min
export function makeFieldIsNotLessThan(min, minFormatted) {
  return (intl, value) => {
    // implies must be number
    const errorIfNotNumber = fieldIsNumber(intl, value);
    if (errorIfNotNumber) {
      return errorIfNotNumber;
    }
    value = Instrument.toAmount(value);
    if (Number(value) >= Number(min)) {
      return undefined;
    }
    if (minFormatted) {
      return intl.formatMessage(
        {
          defaultMessage: 'Minimum: {min}.',
          description:
            'Form field validation failure: number >= min is required (min value preformatted)',
        },
        { min: minFormatted },
      );
    }
    return intl.formatMessage(
      {
        defaultMessage: 'Minimum: {min, number}.',
        description: 'Form field validation failure: number >= min is required',
      },
      { min },
    );
  };
}

// Anything that converts to a number <= max
export function makeFieldIsNotMoreThan(max, maxFormatted) {
  return (intl, value) => {
    // implies must be number
    const errorIfNotNumber = fieldIsNumber(intl, value);
    if (errorIfNotNumber) {
      return errorIfNotNumber;
    }
    value = Instrument.toAmount(value);
    if (Number(value) <= Number(max)) {
      return undefined;
    }
    if (maxFormatted) {
      return intl.formatMessage(
        {
          defaultMessage: 'Maximum: {max}.',
          description:
            'Form field validation failure: number <= max is required (max value preformatted)',
        },
        { max: maxFormatted },
      );
    }
    return intl.formatMessage(
      {
        defaultMessage: 'Maximum: {max, number}.',
        description: 'Form field validation failure: number <= max is required',
      },
      { max },
    );
  };
}

// Anything that converts to a number > min
export function makeFieldIsMoreThan(min, minFormatted) {
  return (intl, value) => {
    // implies must be number
    const errorIfNotNumber = fieldIsNumber(intl, value);
    if (errorIfNotNumber) {
      return errorIfNotNumber;
    }
    value = Instrument.toAmount(value);
    if (Number(value) > Number(min)) {
      return undefined;
    }
    if (minFormatted) {
      return intl.formatMessage(
        {
          defaultMessage: 'Must be greater than {min}.',
          description:
            'Form field validation failure: number > min is required (min value preformatted)',
        },
        { min: minFormatted },
      );
    }
    return intl.formatMessage(
      {
        defaultMessage: 'Must be greater than {min, number}.',
        description: 'Form field validation failure: number > min is required',
      },
      { min },
    );
  };
}
export const fieldIsMoreThan = (amount) => makeFieldIsMoreThan(amount);
export const fieldIsMoreThanZero = makeFieldIsMoreThan(0);

// Any string of max characters or shorter
export function makeFieldIsNotLongerThan(max) {
  return (intl, value) => {
    if (typeof value === 'string' && value.length <= max) {
      return undefined;
    }
    return intl.formatMessage(
      {
        defaultMessage: 'Must be {max, number} characters or fewer.',
        description:
          'Form field validation failure: max characters or fewer required',
      },
      { max },
    );
  };
}

// Any string of min characters or more
export function makeFieldIsNotShorterThan(min) {
  return (intl, value) => {
    if (typeof value === 'string' && value.length >= min) {
      return undefined;
    }
    return intl.formatMessage(
      {
        defaultMessage: 'Must be {min, number} characters or more.',
        description:
          'Form field validation failure: min characters or more required',
      },
      { min },
    );
  };
}

// Any string that _doesn’t_ include a non-alphanumeric character
// Note that this accepts the empty string, and non-strings
export const fieldIsAlphaNumeric = (intl, value) => {
  if (typeof value === 'string' && /[^a-zA-Z0-9 ]/i.test(value)) {
    return intl.formatMessage({
      defaultMessage: 'Only alphanumeric characters allowed.',
      description:
        'Form field validation failure: alphanumeric string is required',
    });
  }
  return undefined;
};

// Any string that looks like an email address
export const fieldIsEmail = (intl, value) => {
  if (typeof value === 'string' && /^.+@.+\..+$/i.test(value)) {
    return undefined;
  }
  return intl.formatMessage({
    defaultMessage: 'Enter a valid email address.',
    description:
      'Form field validation failure: valid email address is required',
  });
};

export const fieldIsInteger = (intl, value) => {
  if (value != round(value))
    return intl.formatMessage({
      defaultMessage: 'Enter a whole number.',
      description: 'Form field validation failure: integer number required',
    });
};

export const useHandleValidator = () => {
  const tokenizedApi = useTokenizedApi();

  const isHandleAvailable = useMemo(
    () => tokenizedApi.account.makeDebouncedHandleAvailabilityChecker(),
    [tokenizedApi.account],
  );

  const validateHandleFormat = (intl, handle) => {
    if (!handle.match(/^[a-zA-Z0-9-]*$/)) {
      return intl.formatMessage({
        defaultMessage: 'Only letters, numbers, and hyphens are allowed.',
        description: 'New account field validation error: invalid characters',
      });
    }
    if (!handle.match(/[a-zA-Z]/)) {
      return intl.formatMessage({
        defaultMessage: 'At least one character must be a letter.',
        description: 'New account field validation error: letter required',
      });
    }
    if (!handle.match(/[a-zA-Z0-9]$/)) {
      return intl.formatMessage({
        defaultMessage: 'Handles cannot end with a special character.',
        description:
          'New account field validation error: final character not letter',
      });
    }
    if (!handle.match(/^[a-zA-Z0-9]/)) {
      return intl.formatMessage({
        defaultMessage: 'Handles cannot begin with a special character.',
        description:
          'New account field validation error: initial character not letter',
      });
    }
    if (handle.length > 25) {
      return intl.formatMessage({
        defaultMessage: 'Handles cannot be longer than 25 characters.',
        description: 'New account field validation error: too long',
      });
    }
  };

  const validateHandleAvailable = useCallback(
    async (intl, handle) => {
      const available = await isHandleAvailable(handle);
      if (!available) {
        return intl.formatMessage({
          defaultMessage: 'This handle is unavailable. Please try another.',
          description: 'New account field validation error: handle in use',
        });
      }
    },
    [isHandleAvailable],
  );
  return useValidators(
    fieldIsRequiredQuiet,
    validateHandleFormat,
    validateHandleAvailable,
  );
};
